// Shared spatial-manager websocket URL helpers.
//
// Kept framework-free so both apps/hub and apps/admin can use them. The key
// contract is that the access token is read LAZILY (via `getUrl`), never
// captured up front — this is what lets websocket reconnects pick up a fresh,
// non-expired token instead of replaying the one from the first render.

/**
 * Builds a spatial-manager websocket URL with the access token plus any extra
 * query params (e.g. `session`, `clientType`).
 */
export const buildWebSocketUrl = (
  baseUrl: string,
  token: string,
  extraParams: Record<string, string> = {},
): string => {
  const webSocketUrl = new URL(baseUrl);

  webSocketUrl.searchParams.append('token', token);

  Object.entries(extraParams).forEach(([key, value]) => {
    webSocketUrl.searchParams.append(key, value);
  });

  return webSocketUrl.toString();
};

/**
 * Returns a `getWebsocketUrl` resolver compatible with react-use-websocket.
 *
 * `getUrl` must read the LIVE token (e.g. from a ref) and return the built URL,
 * or `null` when the token is not yet available — the resolver then polls once
 * a second until it is. Because `getUrl` is invoked on every (re)connect, the
 * token is always read fresh.
 */
export const createWebSocketUrlResolver = (
  getUrl: () => string | null,
): (() => Promise<string>) => {
  return () =>
    new Promise<string>((resolve) => {
      const url = getUrl();

      if (url) {
        resolve(url);
        return;
      }

      const interval = setInterval(() => {
        const nextUrl = getUrl();

        if (nextUrl) {
          clearInterval(interval);
          resolve(nextUrl);
        }
      }, 1000);
    });
};
